import { useNavigate, useParams } from "react-router";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import { useCallback, useEffect, useState } from "react";
import analytics from "../../assets/icons/analytics_icon.svg";
import BRC20Waitlist from "../../components/Reusable/BRC20Waitlist/BRC20Waitlist";
import { useGetOffersBest } from "../../hooks/liquidium/useGetOffersBest";
import Constants, {
  defaultFloorPrice,
  defaultAPY,
  defaultBestOffer,
  defaultDuration,
  defaultLTVRatio,
} from "../../constants";
import { LendingStats, LoanType, SortType } from "../../types";
import { setModalState } from "../../modal";
import BorrowModal from "../../modal/Modals/BorrowModal";
import LendModal from "../../modal/Modals/LendModal/LendModal";
import LoadingBar from "../../components/Reusable/LoadingBar/Loading";
import RowCard from "../../components/Reusable/RowCard/RowCard";
import GenericSelector from "../../components/Reusable/GenericSelector/GenericSelector";
import { convertSatToBtc } from "../../utils";
import ME from "../../components/Reusable/ME/ME";

const Ordinals = () => {
  const [fetchOffersBestRequest, setFetchOffersBestRequest] = useState({
    collateralType: "ORDINAL",
    take: Constants.TAKE_COUNT,
    sortType: SortType.ME_Number_ASC,
  });
  const { loadingOffers, offersBest, error, fetchOffersBest } =
    useGetOffersBest();
  const [options, setOptions] = useState([
    { name: "Borrow", selected: true },
    { name: "Lend", selected: false },
  ]);
  const { collectionSymbol } = useParams<{ collectionSymbol?: string }>();
  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);

  const navigate = useNavigate();

  const handleAnalyticsClick = useCallback(() => {
    navigate("/analytics");
  }, [navigate]);

  //Borrow button
  const handleClick = useCallback(
    (request: LoanType, stats) => {
      setModalState(
        "Borrow",
        () => {},
        () => (
          <BorrowModal
            request={request}
            stats={stats}
            onSubmit={() => fetchOffersBest(fetchOffersBestRequest)}
          />
        ),
        false,
        false,
      );
    },
    [fetchOffersBest, fetchOffersBestRequest],
  );

  //lend modal
  const openLendModal = useCallback(
    (request, stats: LendingStats) => {
      setModalState(
        "Lend",
        () => {},
        () => (
          <LendModal
            type="createOffer"
            request={request}
            stats={stats}
            onSubmit={() => fetchOffersBest(fetchOffersBestRequest)}
          />
        ),
        false,
        false,
      );
    },
    [fetchOffersBest, fetchOffersBestRequest],
  );

  //Fetch offers
  useEffect(() => {
    if (!offersBest) {
      fetchOffersBest(fetchOffersBestRequest);
    }
  }, [fetchOffersBest, fetchOffersBestRequest]);

  //populate lend modal if collection symbol is in the url
  useEffect(() => {
    if (collectionSymbol && offersBest) {
      //NOTE: maybe lets make this a function or hook to set the stats given an offer
      offersBest?.forEach((offer) => {
        const tempFloor = convertSatToBtc(
          false,
          offer.collection ? offer.collection.floorPrice : 0,
        );
        const principal = convertSatToBtc(false, offer.principal);
        if (offer.collectionSymbol === collectionSymbol) {
          openLendModal(offer, {
            bestOffer: { ...defaultBestOffer, value: principal },
            floorPrice: { ...defaultFloorPrice, value: tempFloor },
            LTV: { ...defaultLTVRatio, value: offer.ltv },
            duration: { ...defaultDuration, value: offer.term },
            APY: { ...defaultAPY, value: offer.interestRate },
          });
        }
      });
    }
  }, [collectionSymbol, offersBest, openLendModal]);

  return (
    <div className={" sm:mt-10 mt-6 flex justify-center"}>
      <div className="max-w-[1400px] w-full">
        <div className="flex w-full justify-between items-center">
          <SectionTitle
            title="Ordinals"
            learnMore="https://liquidium.link/faq"
            infoText={
              <>
                <p className="text-gray-400 font-semibold text-xs sm:text-sm mt-2">
                  As a lender, create offers to lend your BTC for interest. As a
                  borrower, you can secure loans against your Ordinals.
                  <br /> Timely repayment ensures lenders recover their BTC plus
                  interest, and borrowers retain their Ordinals.
                  <br /> Not repaying results in the Ordinals being transferred
                  to the lender.
                </p>
              </>
            }
          />
          <button
            className="px-2 py-1 text-gray-50 transition-opacity sm:text-sm text-sm rounded-md font-semibold mt-4 flex items-center gap-1 opacity-70 hover:opacity-100"
            onClick={handleAnalyticsClick}
          >
            <img src={analytics} className="w-5" alt="history icon"></img>
            Analytics
            <div
              className={`rounded-xl h-2 w-2 bg-red-200 mr-[5px] ml-[2px] animate-pulse`}
            ></div>
          </button>
        </div>
        <div className="sm:mt-7 mt-5  mb-10">
          <ME />
          <GenericSelector options={options} setOptions={setOptions} />
          {loadingOffers ? (
            <div className="my-10">
              <LoadingBar />
            </div>
          ) : (
            <div className="mt-10">
              {offersBest
                ?.sort((a, b) => {
                  // Convert the totalVolume string to a number for each offer
                  // @ts-ignore
                  const totalVolumeA = parseFloat(a?.collection?.totalVolume);
                  // @ts-ignore
                  const totalVolumeB = parseFloat(b?.collection?.totalVolume);

                  // Sort in descending order
                  return totalVolumeB - totalVolumeA;
                })
                .map((offer, index) => (
                  <div className="mb-3" key={"borrowItem" + index}>
                    {options.find((option) => option.selected)?.name ===
                    "Borrow" ? (
                      <RowCard
                        disabled={!offer?.principal}
                        type="borrow"
                        offer={offer}
                        onClick={handleClick}
                      />
                    ) : (
                      <RowCard
                        type="lend"
                        offer={offer}
                        onClick={openLendModal}
                        isOrderBook
                        open={index === openRowIndex}
                        onToggle={() =>
                          setOpenRowIndex(index === openRowIndex ? null : index)
                        }
                      />
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ordinals;
